import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatDistanceToNow } from 'date-fns';
import { useLoopComments, useLoopActions } from '@/hooks/use-loops';
import { LoadingFallback } from '@/components/common/LoadingFallback';
import { getThumbnailUrl } from '@/utils/imageUtils';
import VerifiedBadge from '@/components/common/VerifiedBadge';
import { MentionText } from '@/components/mentions/MentionText';
import { useCreateMentions } from '@/hooks/use-mentions';
import { useAuth } from '@/contexts/AuthContext';

interface LoopCommentsProps {
  loopId: string;
}

const LoopComments = ({ loopId }: LoopCommentsProps) => {
  const { user } = useAuth();
  const [newComment, setNewComment] = useState('');
  const { data: comments, isLoading } = useLoopComments(loopId);
  const { addComment, isCommenting } = useLoopActions();
  const { mutate: createMentions } = useCreateMentions();
  
  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim() && user?.id) {
      addComment({ loopId, content: newComment });
      
      // Create mentions for the comment
      // Note: Using loopId as contentId since we don't have the comment ID yet
      // The database trigger will create the notification when the mention is inserted
      if (newComment) {
        createMentions({
          contentType: 'comment',
          contentId: loopId,
          text: newComment,
          mentionerUserId: user.id
        });
      }
      
      setNewComment('');
    }
  };
  
  if (isLoading) {
    return <LoadingFallback />;
  }
  
  return (
    <div className="space-y-3">
      {/* Add comment form */}
      <form onSubmit={handleSubmitComment} className="flex gap-2">
        <Input
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add a comment..."
          className="flex-1"
          disabled={isCommenting}
        />
        <Button
          type="submit"
          size="sm"
          disabled={!newComment.trim() || isCommenting}
        >
          <Send size={16} />
        </Button>
      </form>
      
      {/* Comments list */}
      <div className="space-y-3 max-h-60 overflow-y-auto">
        <AnimatePresence>
          {comments?.map((comment) => (
            <motion.div
              key={comment.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex gap-2"
            >
              <Avatar className="w-6 h-6 flex-shrink-0">
                <AvatarImage src={getThumbnailUrl(comment.user.profile_image_url, 48)} />
                <AvatarFallback className="text-xs">
                  {comment.user.display_name?.[0] || comment.user.username[0]}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="bg-muted rounded-lg p-2">
                  <div className="font-medium text-xs flex items-center gap-1.5">
                    {comment.user.display_name || comment.user.username}
                    {comment.user.is_admin && (
                      <VerifiedBadge type="admin" size={16} className="inline-block" />
                    )}
                    {comment.user.is_partner && (
                      <VerifiedBadge type="partner" size={16} className="inline-block" />
                    )}
                  </div>
                  <div className="text-sm mt-1">
                    <MentionText text={comment.content} />
                  </div>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        
        {comments?.length === 0 && (
          <div className="text-center text-muted-foreground text-sm py-4">
            No comments yet. Be the first to comment!
          </div>
        )}
      </div>
    </div>
  );
};

export default LoopComments;